import { StyledCard } from '@common/Configuration/FormValueConfiguration';
import { Entity } from '@common/types/Entity';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { theme } from '@prophecy/theme';
import { Button, Stack, Text, Tooltip } from '@prophecy/ui-v3';
import { useEffect, useRef } from 'react';

import { DatasetEntityType } from '../../../../../data/apis/transpileTypes';
import { FileFormats } from '../../../config';
import { useImportState } from '../../useImportState';
import { StyledInput, StyledStackContainer } from '../styled';
import { EntityRootHeading, getEntityErrorTitle, useToggleMap } from '../utils';
import { AssociatedEntity } from './AssociatedEntity';
import { EntityHeading } from './EntityHeading';

export const EntityDatasets = ({
  datasets = [],
  hasPipelines: _hasPipelines = false,
  handleDatasetConfig,
  disableConfigButton
}: {
  datasets?: DatasetEntityType[];
  hasPipelines: boolean;
  handleDatasetConfig: (dataset: DatasetEntityType) => void;
  disableConfigButton?: boolean;
}) => {
  const {
    transpilerState: { datasetWithErrors },
    formDataWithActions: {
      formContext: { getValues }
    }
  } = useImportState();

  const isSql = getValues('language') === BackEndCodeLanguage.sql;
  const isAlteryx = getValues('source') === FileFormats.alteryx;

  const { toggleMap, handleToggleEntityState } = useToggleMap();

  const datasetsCount = datasets.length;
  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (datasetsCount > 0 && container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [containerRef, datasetsCount]);

  if (datasetsCount < 1) {
    return null;
  }

  return (
    <Stack width='100%' grow='1' gap={theme.spaces.x16}>
      <EntityRootHeading checkboxDisabled={true} disabled={false} count={datasetsCount} entity={Entity.Dataset} />
      <StyledStackContainer ref={containerRef} gap={theme.spaces.x16} grow='1'>
        {datasets.map((dataset, index) => {
          const toggleKey = 'dataset_' + index;
          const error = datasetWithErrors?.find((p) => p.name === name);
          const hasError = !!error;
          const { name, id, filePath } = dataset;
          const newFilePath = dataset.newDataset?.newDataset?.path;
          const fileIndexKey = index + 'dataset';
          return (
            <Tooltip key={fileIndexKey} title={getEntityErrorTitle(error)}>
              <Stack gap={theme.spaces.x16}>
                <StyledCard hasError={hasError}>
                  <Stack gap={theme.spaces.x16} data-entityname={`${Entity.Dataset}_${name}`}>
                    <EntityHeading
                      entity={Entity.Dataset}
                      checkboxDisabled={true}
                      enabled={true}
                      onChange={() => null}
                      title={name}
                      hasToggle={!toggleMap[toggleKey]}
                      actions={
                        <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
                          {toggleMap[toggleKey] && !isSql && isAlteryx ? (
                            <Button
                              variant='secondaryGray'
                              onClick={() => handleDatasetConfig(dataset)}
                              data-test-id='entity-datasets-configure-button'>
                              Configure
                            </Button>
                          ) : null}
                          {!dataset.schema && (
                            <Text level='sm13' tone={theme.colors.warning500}>
                              Missing schema
                            </Text>
                          )}
                        </Stack>
                      }
                      toggle={() => handleToggleEntityState(toggleKey)}
                    />
                    {toggleMap[toggleKey] ? null : (
                      <>
                        {filePath && (
                          <Stack direction='horizontal' gap='24px' alignY='end'>
                            <StyledInput
                              value={newFilePath || filePath}
                              label='Path'
                              tooltip={getEntityErrorTitle(error)}
                              disabled={true}
                              data-test-id='transpiler-entity-datasets-path-input'
                            />
                            {!isSql && isAlteryx ? (
                              <Button
                                variant='secondaryGray'
                                onClick={() => handleDatasetConfig(dataset)}
                                disabled={disableConfigButton}
                                tooltip={disableConfigButton ? 'Infer schema cancel in progress' : null}
                                data-test-id='entity-datasets-configure-path-button'>
                                Configure
                              </Button>
                            ) : null}
                          </Stack>
                        )}
                        <AssociatedEntity parentEntity={Entity.Dataset} entity={Entity.Pipeline} path={id} />
                      </>
                    )}
                  </Stack>
                </StyledCard>
              </Stack>
            </Tooltip>
          );
        })}
      </StyledStackContainer>
    </Stack>
  );
};
